import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { usStates } from '../utils/states';
import { countries } from '../utils/countries';
import styles from "../styles/RegistrationPage.module.css";
import "../styles/LoginPage.css";

function ConfirmationModal({ message, onClose }) {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2>Registration Submitted</h2>
        <p>{message}</p>
        <button onClick={onClose} className={styles.modalButton}>
          Go to Login
        </button>
      </div>
    </div>
  );
}

export default function RegistrationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { provisionalUser, registerUser } = useAuth();
  const [formData, setFormData] = useState({
    name: provisionalUser?.name || "",
    birthday: "",
    country: "United States",
    custom_country: "",
    job_title: "",
    state_province: "",
    custom_state_province: "",
    company_affiliate: "",
    exp_ml: 0,
    exp_dl: 0,
    exp_llm: 0,
    exp_vlm: 0,
    exp_agentic: 0,
    referred_by: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    // Prepare data for submission
    const submissionData = { ...formData };
    
    // Handle custom country
    if (submissionData.country === "Other") {
        submissionData.country = submissionData.custom_country;
    }
    delete submissionData.custom_country;

    // Handle custom state/province
    if (submissionData.country !== "United States") {
        submissionData.state_province = submissionData.custom_state_province;
    }
    delete submissionData.custom_state_province;


    const result = await registerUser(submissionData);

    setIsSubmitting(false);

    if (result.error) {
      setError(result.error);
    } else if (result.pending) {
      setConfirmationMessage(result.message);
      setShowConfirmation(true);
    } else if (result.success) {
      // show_tutorial is set server-side; AboutSection will open it when user loads
    }
  };

  const handleModalClose = () => {
    setShowConfirmation(false);
    // Navigate to the intended deep-link destination if threaded through state, else "/".
    const redirectTo = location.state?.redirectTo;
    navigate(redirectTo?.startsWith('/') ? redirectTo : "/chat", { replace: true });
  };

  if (!provisionalUser) {
    return (
      <div className={styles.container}>
        <p>
          No registration data found. Please start the sign-in process again.
        </p>
      </div>
    );
  }

  const experienceOptions = [
    { label: "0-1 years", value: 1 },
    { label: "1-3 years", value: 2 },
    { label: "3-5 years", value: 3 },
    { label: "5+ years", value: 4 },
  ];

  return (
    <>
      {showConfirmation && (
        <ConfirmationModal
          message={confirmationMessage}
          onClose={handleModalClose}
        />
      )}
      <div className={styles.container}>
        <div className={styles.formCard}>
          <h2>Complete Your Registration</h2>
          <p>
            Welcome, {provisionalUser.name || provisionalUser.email}! Please provide some additional
            details.
          </p>
          <form onSubmit={handleSubmit}>
            <div className={styles.formField}>
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <h3 style={{ marginTop: '20px', marginBottom: '20px', color: '#e0e0e0' }}>OPTIONAL QUESTIONNAIRE</h3>

            {/* 
            <div className={styles.formField}>
              <label htmlFor="birthday">Birthday</label>
              <input
                type="date"
                id="birthday"
                name="birthday"
                value={formData.birthday}
                onChange={handleChange}
                required
              />
            </div>
            */}

            <div className={styles.formField}>
              <label htmlFor="referred_by">
                Referred By (Referral from approved user can expedite approval process)
              </label>
              <input
                type="email"
                id="referred_by"
                name="referred_by"
                value={formData.referred_by}
                onChange={handleChange}
                placeholder="referrer@example.com"
                style={{ marginTop: '10px' }}
              />
            </div>

            <div className={styles.formField}>
              <label htmlFor="country">
                Country:
                <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    style={{ marginLeft: '10px' }}
                >
                    {countries.map((c) => (
                        <option key={c.value} value={c.label}>{c.label}</option>
                    ))}
                </select>
              </label>
            </div>

            {formData.country === "Other" && (
                <div className={styles.formField}>
                    <label htmlFor="custom_country">Enter Country</label>
                    <input
                        type="text"
                        id="custom_country"
                        name="custom_country"
                        value={formData.custom_country}
                        onChange={handleChange}
                    />
                </div>
            )}

            <div className={styles.formField}>
              <label>
                Prefecture/Province/State:
                {formData.country === "United States" ? (
                    <select
                        name="state_province"
                        value={formData.state_province}
                        onChange={handleChange}
                        style={{ marginLeft: '10px' }}
                        >
                    <option value="">Select a state</option>
                    {usStates.map((s) => (
                        <option key={s.abbreviation} value={s.abbreviation}>{s.name}</option>
                    ))}
                    </select>
                ) : (
                    <input
                        type="text"
                        name="custom_state_province"
                        value={formData.custom_state_province}
                        onChange={handleChange}
                        placeholder="Enter state/province/region"
                        style={{ marginTop: '5px' }}
                    />
                )}
              </label>
            </div>
            <div className={styles.formField}>
              <label htmlFor="job_title">Job Title</label>
              <input
                type="text"
                id="job_title"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
              />
            </div>
            <div className={styles.formField}>
              <label htmlFor="company_affiliate">Company/Affiliation</label>
              <input
                type="text"
                id="company_affiliate"
                name="company_affiliate"
                value={formData.company_affiliate}
                onChange={handleChange}
              />
            </div>

            {[
              { name: "exp_ml", label: "ML Experience" },
              { name: "exp_dl", label: "DL Experience" },
              { name: "exp_llm", label: "LLM Experience" },
              { name: "exp_vlm", label: "VLM Experience" },
              { name: "exp_agentic", label: "Agentic Systems Experience" },
            ].map(({ name, label }) => (
              <div className={styles.formField} key={name}>
                <label htmlFor={name}>{label}:
                    <select
                      id={name}
                      name={name}
                      value={formData[name]}
                      onChange={handleChange}
                      style={{ marginLeft: '10px' }}
                    >
                      <option value={0} disabled>Select experience level</option>
                      {experienceOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                </label>
              </div>
            ))}

            {error && <p className={styles.error}>{error}</p>}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                  Submitting...
                </div>
              ) : "Submit"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
